<template>
  <Teleport to="body">
    <div
      class="modal fade"
      id="userGroupSwitcherModal"
      tabindex="-1"
      aria-labelledby="userGroupSwitcherModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered modal-xl company-entity-dialog">
        <div class="modal-content company-entity-modal">
          <div class="modal-header company-entity-header">
            <div>
              <span class="company-entity-eyebrow">Workspace context</span>
              <h5 class="modal-title" id="userGroupSwitcherModalLabel">
                <i class="icofont icofont-company me-2"></i>
                Switch Company Entity
              </h5>
            </div>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>

          <div class="modal-body company-entity-body">
            <!-- ryd user group: no switching at all, only this warning (rp-users rule R4b) -->
            <div v-if="isRydGroupUser" class="alert alert-light border border-warning mb-0" role="alert">
              <h6 class="alert-heading">Switching Is Not Available With The ryd User Group</h6>
              <p class="mb-2">
                ryd is excluded from company entity switching. Your user is in the ryd user group
                or has ryd among its company entities, so you cannot switch. To work as an external
                user group, create a second user in that group, give it the User Group Switcher role
                and add the external user groups there.
              </p>
              <router-link
                to="/users/guide#guides"
                target="_blank"
                rel="noopener"
                class="alert-link"
                >User &amp; Role Management Guide</router-link
              >
            </div>

            <div class="entity-context-strip" v-if="organization || isRydUser">
              <div v-if="organization" class="entity-context-item">
                <span class="entity-context-label">Organization</span>
                <span class="entity-context-value">{{ organization }}</span>
              </div>
              <div v-if="isRydUser" class="entity-context-item">
                <span class="entity-context-label">Access mode</span>
                <span class="entity-context-value">Internal role selection</span>
              </div>
            </div>

            <p v-if="!isRydGroupUser" class="entity-intro">
              <span v-if="isRydUser">
                Choose the company entity you want to work as, then confirm which access profile
                should be active for this session.
              </span>
              <span v-else>
                Choose a company entity to switch your portal context. Access permissions are
                already configured for each entity.
              </span>
            </p>

            <!-- Loading State -->
            <div v-if="loading" class="text-center py-4" style="position: relative; z-index: 1">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-2 text-muted">Loading company entities...</p>
            </div>

            <!-- Error State -->
            <div
              v-else-if="error"
              class="alert alert-danger"
              style="position: relative; z-index: 1"
            >
              <i class="fa fa-exclamation-triangle me-2"></i>
              {{ error }}
            </div>

            <!-- RYD User: Company Entity Autocomplete -->
            <div v-if="!loading && !error && isRydUser" class="mb-4">
              <div class="mb-3">
                <label class="form-label fw-bold" for="companyEntitySearch"> Company entity </label>
                <div class="dropdown position-relative company-entity-autocomplete">
                  <input
                    id="companyEntitySearch"
                    type="text"
                    class="form-control company-entity-search"
                    v-model="userGroupSearchTerm"
                    @keyup.enter="handleEnterOnUserGroup"
                    placeholder="Search company entities..."
                    @focus="showUserGroupDropdown = true"
                  />
                  <ul
                    class="dropdown-menu w-100 company-entity-dropdown"
                    :class="{ show: showUserGroupDropdown }"
                  >
                    <li v-if="isLoadingUserGroups" class="dropdown-item text-muted">
                      <span
                        class="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Loading...
                    </li>
                    <li
                      v-else-if="!allUserGroups || allUserGroups.length === 0"
                      class="dropdown-item text-muted"
                    >
                      No company entities available
                    </li>
                    <li
                      v-else-if="filteredUserGroups.length === 0"
                      class="dropdown-item text-muted"
                    >
                      No company entities found
                    </li>
                    <li
                      v-else
                      v-for="group in filteredUserGroups"
                      :key="group.partnerName"
                      @click="selectUserGroupFromAutocomplete(group)"
                      class="dropdown-item cursor-pointer custom-dropdown-item"
                    >
                      <div class="d-flex justify-content-between align-items-center">
                        <span class="sponsor-name">{{ group.partnerName }}</span>
                        <i class="fa fa-arrow-right entity-option-icon"></i>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>

              <!-- Show selected company entity info -->
              <div v-if="selectedUserGroup" class="selected-entity-summary">
                <i class="fa fa-check-circle me-2"></i>
                <div>
                  <span class="selected-entity-label">Selected company entity</span>
                  <strong>{{ selectedEntityName }}</strong>
                </div>
              </div>
            </div>

            <!-- Non-RYD User: Company Entity List -->
            <div v-else-if="!loading && !error && !isRydGroupUser" class="company-entity-list">
              <div v-if="userGroups.length === 0" class="empty-entity-state">
                <i class="fa fa-building-o"></i>
                <span>No company entities are available for switching.</span>
              </div>
              <div
                v-else
                v-for="userGroup in userGroups"
                :key="userGroup.userGroupId"
                class="company-entity-card"
                :class="{
                  'company-entity-card--current': userGroup.userGroupId === currentUserGroupId,
                  'company-entity-card--selected':
                    selectedUserGroup &&
                    selectedUserGroup.userGroupId === userGroup.userGroupId &&
                    userGroup.userGroupId !== currentUserGroupId,
                  'cursor-pointer': userGroup.userGroupId !== currentUserGroupId,
                }"
                @click="selectUserGroup(userGroup)"
              >
                <div class="d-flex justify-content-between align-items-start">
                  <div class="flex-grow-1">
                    <h6 class="mb-2 company-entity-card-title">
                      {{ userGroup.displayName }}
                      <span
                        v-if="userGroup.userGroupId === currentUserGroupId"
                        class="badge rounded-pill text-bg-success ms-2"
                      >
                        <i class="fa fa-check me-1"></i>Current
                      </span>
                    </h6>

                    <div class="company-entity-details">
                      <div class="mb-2">
                        <small class="text-muted">Entity identifier:</small>
                        <code class="ms-2">{{ userGroup.userGroup }}</code>
                      </div>

                      <div
                        class="mb-2"
                        v-if="Array.isArray(userGroup.partnerTypes) && userGroup.partnerTypes.length > 0"
                      >
                        <small class="text-muted">Partner Types:</small>
                        <span
                          v-for="type in userGroup.partnerTypes"
                          :key="type"
                          class="badge bg-info ms-1"
                        >
                          {{ formatPartnerType(type) }}
                        </span>
                      </div>

                      <div v-if="!isRydUser && Array.isArray(userGroup.roles) && userGroup.roles.length > 0">
                        <small class="text-muted">Roles:</small>
                        <span
                          v-for="(role, index) in userGroup.roles.slice(0, 3)"
                          :key="role"
                          class="badge bg-secondary ms-1"
                        >
                          {{ formatRole(role) }}
                        </span>
                        <span v-if="userGroup.roles.length > 3" class="text-muted ms-1">
                          +{{ userGroup.roles.length - 3 }} more
                        </span>
                      </div>
                    </div>
                  </div>

                  <div v-if="userGroup.userGroupId !== currentUserGroupId">
                    <i class="fa fa-arrow-right company-entity-card-arrow"></i>
                  </div>
                </div>
              </div>
            </div>

            <!-- RYD User: Access Profile Selection -->
            <div
              v-if="
                isRydUser &&
                !loading &&
                !error &&
                selectedUserGroup &&
                (selectedUserGroup.userGroupId !== currentUserGroupId || !currentUserGroupId)
              "
              class="mt-4"
            >
              <div class="access-profile-panel">
                <div class="access-profile-header">
                  <div>
                    <span class="company-entity-eyebrow">Access profile</span>
                    <h6>
                      {{ selectedEntityName }}
                    </h6>
                  </div>
                  <span class="badge rounded-pill text-bg-light">
                    {{ selectedRoles.length }} role{{
                      selectedRoles.length === 1 ? '' : 's'
                    }}
                    active
                  </span>
                </div>
                <div class="access-profile-body">
                  <p class="text-muted small mb-3">
                    Review the permissions that will be active after switching. Roles are selected
                    by default from your current account access.
                  </p>

                  <!-- Roles Selection - Show all assigned roles to logged-in user -->
                  <div class="mb-4">
                    <div class="d-flex justify-content-between align-items-center gap-2 mb-2">
                      <h6 class="fw-bold mb-0">Roles</h6>
                      <div v-if="loggedInUserRoles.length > 0" class="btn-group btn-group-sm">
                        <button
                          type="button"
                          class="btn btn-outline-primary"
                          @click="selectAllRoles"
                        >
                          Select all
                        </button>
                        <button type="button" class="btn btn-outline-secondary" @click="clearRoles">
                          Clear
                        </button>
                      </div>
                    </div>
                    <div v-if="loggedInUserRoles.length === 0" class="alert alert-info">
                      <i class="fa fa-info-circle me-2"></i>
                      No roles assigned to your account.
                    </div>
                    <template v-else>
                      <input
                        type="text"
                        class="form-control form-control-sm mb-2"
                        v-model="roleSearchTerm"
                        placeholder="Search roles..."
                        aria-label="Search roles"
                      />
                      <div v-if="filteredRoles.length === 0" class="text-muted small py-2">
                        No roles match "{{ roleSearchTerm }}".
                      </div>
                      <div v-else class="permission-grid">
                        <label v-for="role in filteredRoles" :key="role" class="permission-option">
                          <input
                            type="checkbox"
                            class="form-check-input"
                            :id="`role-${role}`"
                            :value="role"
                            v-model="selectedRoles"
                          />
                          <span>{{ formatRole(role) }}</span>
                        </label>
                      </div>
                    </template>
                    <div
                      v-if="selectedRoles.length === 0 && loggedInUserRoles.length > 0"
                      class="alert alert-warning mt-2"
                    >
                      <i class="fa fa-exclamation-triangle me-2"></i>
                      Please select at least one role.
                    </div>
                  </div>

                  <!-- Partner Types Selection - Show all partner types -->
                  <div>
                    <div class="d-flex justify-content-between align-items-center gap-2 mb-2">
                      <h6 class="fw-bold mb-0">Partner types</h6>
                      <div v-if="allPartnerTypes.length > 0" class="btn-group btn-group-sm">
                        <button
                          type="button"
                          class="btn btn-outline-primary"
                          @click="selectAllPartnerTypes"
                        >
                          Select all
                        </button>
                        <button
                          type="button"
                          class="btn btn-outline-secondary"
                          @click="clearPartnerTypes"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                    <div v-if="allPartnerTypes.length === 0" class="alert alert-info">
                      <i class="fa fa-info-circle me-2"></i>
                      No partner types available.
                    </div>
                    <div v-else class="permission-grid">
                      <label
                        v-for="partnerType in allPartnerTypes"
                        :key="partnerType"
                        class="permission-option"
                      >
                        <input
                          type="checkbox"
                          class="form-check-input"
                          :id="`pt-${partnerType}`"
                          :value="partnerType"
                          v-model="selectedPartnerTypes"
                        />
                        <span>{{ formatPartnerType(partnerType) }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              {{ isRydGroupUser ? 'Close' : 'Cancel' }}
            </button>
            <button
              v-if="!isRydGroupUser"
              type="button"
              class="btn btn-primary"
              @click="confirmSwitch"
              :disabled="!canSwitch"
            >
              <span v-if="switching" class="spinner-border spinner-border-sm me-2"></span>
              <i v-else class="fa fa-shuffle me-2"></i>
              Switch Entity
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { useToast } from 'vue-toastification';
import { userGroupService } from '@/services/userGroupService';
import { Modal } from 'bootstrap';
import {
  canSwitchCompanyEntity,
  getUserAddDataFromLocalStorage,
  isCompanySwitchBlocked,
} from '@/utils/userUtils';
import { fetchPartnerNames } from '@/utils/partnerNamesUtils';
import { USER_GROUP_RYD } from '@/constants/constants';

export default {
  name: 'UserGroupSwitcher',
  setup() {
    return { toast: useToast() };
  },
  data() {
    return {
      modal: null,
      loading: false,
      switching: false,
      error: null,
      organization: null,
      userGroups: [],
      currentUserGroupId: null,
      selectedUserGroup: null,
      isRydUser: false,
      isRydGroupUser: false,
      masterRoles: [],
      selectedRoles: [],
      allPartnerTypes: [],
      selectedPartnerTypes: [],
      // For RYD users autocomplete
      allUserGroups: [], // All possible user groups (partner names)
      userGroupSearchTerm: '',
      showUserGroupDropdown: false,
      isLoadingUserGroups: false,
      loggedInUserRoles: [],
      loggedInUserPartnerTypes: [],
      roleSearchTerm: '',
      clickOutsideHandler: null,
    };
  },
  computed: {
    canSwitch() {
      if (!this.selectedUserGroup || this.switching) {
        return false;
      }
      if (this.selectedUserGroup.userGroupId === this.currentUserGroupId) {
        return false;
      }
      // For RYD users, require at least one role selected
      if (this.isRydUser && this.selectedRoles.length === 0) {
        return false;
      }
      return true;
    },
    // Filter user groups based on search term for autocomplete
    filteredUserGroups() {
      if (!this.userGroupSearchTerm || !this.allUserGroups) {
        return this.allUserGroups || [];
      }
      const searchTerm = this.normalizeSearch(this.userGroupSearchTerm);
      return this.allUserGroups.filter((group) =>
        this.normalizeSearch(group.partnerName).includes(searchTerm),
      );
    },
    selectedEntityName() {
      return this.selectedUserGroup?.displayName || this.selectedUserGroup?.userGroup || '';
    },
    sortedRoles() {
      return [...this.loggedInUserRoles].sort((a, b) =>
        this.formatRole(a).localeCompare(this.formatRole(b)),
      );
    },
    filteredRoles() {
      const searchTerm = this.normalizeSearch(this.roleSearchTerm);
      if (!searchTerm) {
        return this.sortedRoles;
      }
      return this.sortedRoles.filter((role) =>
        this.normalizeSearch(this.formatRole(role)).includes(searchTerm),
      );
    },
  },
  mounted() {
    const modalElement = document.getElementById('userGroupSwitcherModal');
    this.modal = new Modal(modalElement, {
      backdrop: true,
      keyboard: true,
      focus: true,
    });

    // Close dropdown when clicking outside
    this.clickOutsideHandler = (e) => {
      if (!e.target.closest('.company-entity-autocomplete')) {
        this.showUserGroupDropdown = false;
      }
    };
    document.addEventListener('click', this.clickOutsideHandler);
  },
  beforeUnmount() {
    // Clean up event listener
    if (this.clickOutsideHandler) {
      document.removeEventListener('click', this.clickOutsideHandler);
    }
  },
  methods: {
    async open() {
      this.error = null;
      this.loading = true;
      this.selectedUserGroup = null;
      this.selectedRoles = [];
      this.selectedPartnerTypes = [];
      this.userGroupSearchTerm = '';
      this.roleSearchTerm = '';
      this.showUserGroupDropdown = false;

      // Get logged-in user data
      const { userGroup, roles, partnerTypes, isUserGroupRyd, availableUserGroups } =
        getUserAddDataFromLocalStorage();

      this.isRydGroupUser = isCompanySwitchBlocked(userGroup, availableUserGroups);
      if (this.isRydGroupUser) {
        this.loading = false;
        this.isRydUser = false;
        this.modal.show();
        return;
      }
      // Client mirror of rp-users rule R4: without the role the server refuses the list and the
      // switch, so there is nothing to load. Reached only when the menu entry guard was bypassed.
      if (!canSwitchCompanyEntity({ roles })) {
        this.error = 'Switching company entities requires the User Group Switcher role.';
        this.loading = false;
        this.modal.show();
        return;
      }
      // Dedupe: stored roles can contain duplicates, which breaks the v-for :key
      this.loggedInUserRoles = [...new Set(roles || [])];
      this.loggedInUserPartnerTypes = [...new Set(partnerTypes || [])];

      // Initial check: internal RYD user only if userGroup === 'ryd' or has 'ryd' in availableUserGroups
      const hasRydInAvailableGroups =
        Array.isArray(availableUserGroups) &&
        availableUserGroups.some((ug) => (ug.userGroup || '').toLowerCase() === USER_GROUP_RYD);
      this.isRydUser = Boolean(
        isUserGroupRyd ||
          (userGroup && userGroup.toLowerCase() === USER_GROUP_RYD) ||
          hasRydInAvailableGroups,
      );

      // Show modal
      this.modal.show();

      try {
        // Fetch organization and current user group info (needed for both RYD and non-RYD users)
        const response = await userGroupService.getAvailableUserGroups();
        this.organization = response.organization;
        this.currentUserGroupId = response.currentUserGroupId;
        this.masterRoles = response.masterRoles || [];
        this.allPartnerTypes = response.allPartnerTypes || [];

        // Synchronize with authoritative backend isRydUser flag if present
        if (typeof response.isRydUser === 'boolean') {
          this.isRydUser = response.isRydUser;
        }

        // If RYD user, fetch all possible user groups (partner names) for autocomplete
        if (this.isRydUser) {
          await this.loadAllUserGroups();
        } else {
          // For non-RYD users, use the user groups from the response
          this.userGroups = response.userGroups || [];
        }
      } catch (error) {
        console.error('Error loading user groups:', error);
        // Handle different error response structures
        if (error.response?.data) {
          this.error =
            error.response.data.message ||
            error.response.data.details?.message ||
            'Failed to load company entities. Please try again.';
        } else if (error.message) {
          this.error = error.message;
        } else {
          this.error = 'Failed to load company entities. Please try again.';
        }
      } finally {
        this.loading = false;
      }
    },

    async loadAllUserGroups() {
      this.isLoadingUserGroups = true;
      try {
        this.allUserGroups = await fetchPartnerNames();
      } catch (error) {
        console.error('Error loading company entities:', error);
        this.allUserGroups = [];
      } finally {
        this.isLoadingUserGroups = false;
      }
    },

    selectUserGroupFromAutocomplete(partner) {
      if (partner && partner.partnerName) {
        this.userGroupSearchTerm = partner.partnerName;
        // Create a user group object from the selected partner
        this.selectedUserGroup = {
          userGroupId: partner.partnerName, // Use partnerName as ID
          displayName: partner.partnerName,
          userGroup: partner.partnerName,
          partnerTypes: [],
          roles: [],
        };

        this.selectedRoles = [...this.loggedInUserRoles];
        this.selectedPartnerTypes = [...this.allPartnerTypes];
      } else {
        this.userGroupSearchTerm = '';
        this.selectedUserGroup = null;
      }
      this.showUserGroupDropdown = false;
    },

    handleEnterOnUserGroup() {
      const exactMatch = this.filteredUserGroups.find(
        (group) =>
          this.normalizeSearch(group.partnerName) ===
          this.normalizeSearch(this.userGroupSearchTerm),
      );
      const nextSelection = exactMatch || this.filteredUserGroups[0];

      if (this.showUserGroupDropdown && nextSelection) {
        this.selectUserGroupFromAutocomplete(nextSelection);
      }

      this.showUserGroupDropdown = false;
    },

    selectUserGroup(userGroup) {
      // Don't allow selecting the current user group
      if (userGroup.userGroupId === this.currentUserGroupId) {
        return;
      }
      this.selectedUserGroup = userGroup;

      if (this.isRydUser) {
        this.selectedRoles = [...this.loggedInUserRoles];
        this.selectedPartnerTypes = [...this.allPartnerTypes];
      }
    },

    async confirmSwitch() {
      if (!this.canSwitch) {
        return;
      }

      this.switching = true;
      this.error = null;

      try {
        const response = await userGroupService.switchUserGroup(
          this.selectedUserGroup.userGroupId,
          this.isRydUser ? this.selectedRoles : undefined,
          this.isRydUser ? this.selectedPartnerTypes : undefined,
        );

        if (response.success && response.userData) {
          // Update local storage with new user data
          const userData = await userGroupService.fetchEnrichedUserData(response.userData);
          userGroupService.updateLocalStorage(userData);

          this.toast.success(`Switched to ${this.selectedEntityName}`);

          // Close modal
          this.modal.hide();

          // Always land on the homepage: the current page may not exist (or be
          // permitted) in the new company context. A full page load, not a router
          // push, so nothing cached for the previous company stays behind.
          setTimeout(() => {
            window.location.assign('/');
          }, 500);
        }
      } catch (error) {
        console.error('Error switching company entity:', error);
        this.error =
          error.response?.data?.message || 'Failed to switch company entity. Please try again.';

        this.toast.error(this.error);
      } finally {
        this.switching = false;
      }
    },

    selectAllRoles() {
      // With an active search, only affect the visible roles
      this.selectedRoles = [...new Set([...this.selectedRoles, ...this.filteredRoles])];
    },

    clearRoles() {
      this.selectedRoles = this.selectedRoles.filter((role) => !this.filteredRoles.includes(role));
    },

    selectAllPartnerTypes() {
      this.selectedPartnerTypes = [...this.allPartnerTypes];
    },

    clearPartnerTypes() {
      this.selectedPartnerTypes = [];
    },

    normalizeSearch(value) {
      return (value || '').toString().trim().toLowerCase();
    },

    formatPartnerType(type) {
      // Convert FLEET_PARTNER to Fleet Partner
      return type
        .split('_')
        .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
        .join(' ');
    },

    formatRole(role) {
      // Roles use mixed naming: camelCase (userManagementAdmin), snake_Case
      // (fleet_Temp_Password_Viewer) and lowercase snake (gdpr_admin).
      const acronyms = ['api', 'gdpr'];
      return role
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .split(/[\s_]+/)
        .map((word) => {
          const lower = word.toLowerCase();
          if (acronyms.includes(lower)) return lower.toUpperCase();
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(' ');
    },
  },
};
</script>

<style scoped>
.company-entity-dialog {
  max-width: min(960px, calc(100vw - 32px));
}

.company-entity-modal {
  border: 0;
  border-radius: 12px;
  box-shadow: 0 24px 70px rgba(22, 28, 45, 0.24);
  overflow: visible;
}

.company-entity-header {
  align-items: flex-start;
  background: #ffffff;
  border-bottom: 1px solid #e7ebf3;
  padding: 24px 28px 18px;
}

.company-entity-header .modal-title {
  color: #202532;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 1.2;
  margin: 4px 0 0;
}

.company-entity-eyebrow {
  color: #667085;
  display: block;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.company-entity-body {
  padding: 24px 28px;
}

.entity-context-strip {
  background: #f7f9fc;
  border: 1px solid #e1e7f0;
  border-radius: 8px;
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  margin-bottom: 18px;
  padding: 14px 16px;
}

.entity-context-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.entity-context-label,
.selected-entity-label {
  color: #667085;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}

.entity-context-value {
  color: #202532;
  font-weight: 700;
}

.entity-intro {
  color: #5f6675;
  font-size: 15px;
  line-height: 1.5;
  margin-bottom: 20px;
}

.company-entity-search {
  border-color: #ccd5e1;
  border-radius: 8px;
  font-size: 15px;
  min-height: 46px;
}

.company-entity-search:focus {
  border-color: #0d6efd;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.12);
}

.company-entity-dropdown {
  border: 1px solid #d7deea;
  border-radius: 8px;
  box-shadow: 0 16px 36px rgba(22, 28, 45, 0.16);
  max-height: 300px;
  overflow-y: auto;
  padding: 6px;
  z-index: 1070;
}

.custom-dropdown-item {
  border-radius: 6px;
  color: #263143;
  padding: 10px 12px;
}

.custom-dropdown-item:hover,
.custom-dropdown-item:focus {
  background-color: #f1f5fb;
}

.entity-option-icon {
  color: #8b95a7;
  font-size: 12px;
}

.selected-entity-summary {
  align-items: center;
  background: #eef7f4;
  border: 1px solid #bee2d7;
  border-radius: 8px;
  color: #204c42;
  display: flex;
  gap: 8px;
  padding: 12px 14px;
}

.selected-entity-summary strong {
  display: block;
}

.company-entity-list {
  display: grid;
  gap: 12px;
}

.empty-entity-state {
  align-items: center;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  color: #667085;
  display: flex;
  gap: 10px;
  justify-content: center;
  min-height: 96px;
  padding: 18px;
  text-align: center;
}

.company-entity-card {
  background: #ffffff;
  border: 1px solid #d9e0eb;
  border-radius: 8px;
  padding: 16px;
  transition:
    border-color 0.16s ease,
    box-shadow 0.16s ease,
    transform 0.16s ease;
}

.company-entity-card:hover:not(.company-entity-card--current) {
  border-color: #0d6efd;
  box-shadow: 0 10px 24px rgba(22, 28, 45, 0.09);
  transform: translateY(-1px);
}

.company-entity-card--current {
  background: #f3faf7;
  border-color: #7fc3ad;
}

.company-entity-card--selected {
  background: #f4f8ff;
  border-color: #5f8ee8;
  box-shadow: 0 0 0 3px rgba(95, 142, 232, 0.12);
}

.company-entity-card-title {
  color: #202532;
  font-size: 16px;
  font-weight: 700;
}

.company-entity-details {
  color: #485266;
}

.company-entity-card-arrow {
  color: #5f8ee8;
  margin-top: 4px;
}

.access-profile-panel {
  border: 1px solid #d9e0eb;
  border-radius: 10px;
  overflow: hidden;
}

.access-profile-header {
  align-items: center;
  background: #24324b;
  color: #ffffff;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
}

.access-profile-header .company-entity-eyebrow {
  color: #c9d4e8;
}

.access-profile-header h6 {
  color: #ffffff;
  font-size: 17px;
  font-weight: 700;
  margin: 2px 0 0;
}

.access-profile-body {
  background: #ffffff;
  padding: 18px;
}

.permission-grid {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.permission-option {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e4e9f2;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  gap: 10px;
  min-height: 42px;
  padding: 9px 10px;
}

.permission-option:hover {
  background: #f2f6fb;
}

.permission-option span {
  color: #2f394a;
  font-weight: 600;
  line-height: 1.3;
}

code {
  font-size: 0.875rem;
  padding: 0.125rem 0.25rem;
  background-color: #f8f9fa;
  border-radius: 0.25rem;
}

.cursor-pointer {
  cursor: pointer;
}

@media (max-width: 575.98px) {
  .company-entity-dialog {
    max-width: calc(100vw - 16px);
  }

  .company-entity-header,
  .company-entity-body {
    padding-left: 18px;
    padding-right: 18px;
  }

  .company-entity-header .modal-title {
    font-size: 20px;
  }

  .access-profile-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .permission-grid {
    grid-template-columns: 1fr;
  }
}
</style>
